import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import YouTubeIcon from "@mui/icons-material/YouTube";

export function VideoPlayer({
  videoLink,
  image = { src: "/assets/video-placeholder.jpg", alt: "video image" },
  title,
  className = "",
  onPlay,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => {
    setIsOpen(true);
    if (onPlay) {
      onPlay();
    }
  };

  // Convert YouTube watch URL to embed URL
  const embedVideoLink = videoLink.includes("youtube.com/watch")
    ? videoLink.replace("watch?v=", "embed/")
    : videoLink.includes("youtu.be/")
    ? `https://www.youtube.com/embed/${
        videoLink.split("youtu.be/")[1]?.split("?")[0]
      }`
    : videoLink;

  // Extract YouTube video thumbnail
  const getVideoThumbnail = () => {
    if (videoLink.includes("youtube.com/watch?v=")) {
      const videoId = new URL(videoLink).searchParams.get("v");
      if (videoId) {
        return {
          src: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
          alt: "Video thumbnail",
        };
      }
    } else if (videoLink.includes("youtu.be/")) {
      const videoId = videoLink.split("youtu.be/")[1]?.split("?")[0];
      if (videoId) {
        return {
          src: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
          alt: "Video thumbnail",
        };
      }
    }
    return image;
  };

  const thumbnailImage = getVideoThumbnail();

  return (
    <>
      <Box
        className={className}
        sx={{
          backgroundColor: "white",
          border: "1px solid var(--border-color)",
          borderRadius: "var(--border-radius)",
          boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
          overflow: "visible",
          cursor: "pointer",
          position: "relative",
        }}
        onClick={handleOpen}
      >
        {title && (
          <Box sx={{ p: 1.5, display: "flex", alignItems: "center" }}>
            <YouTubeIcon
              sx={{ color: "#37474F", fontSize: "24px", mr: 1.875, ml: 1 }}
            />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
        )}
        <img
          src={thumbnailImage.src}
          alt={thumbnailImage.alt}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            borderBottomLeftRadius: "var(--border-radius)",
            borderBottomRightRadius: "var(--border-radius)",
          }}
        />
        <Box
          className="play-button"
          sx={{
            position: "absolute",
            top: "55%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Button
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #2c3e50, #1a252f)",
              color: "white",
              px: { xs: 2, md: 3 },
              py: 1,
              gap: 1,
              display: "flex",
              alignItems: "center",
              borderRadius: "50px",
              fontSize: { xs: "14px", md: "16px" },
              fontWeight: 600,
              boxShadow: "0 4px 20px rgba(44, 62, 80, 0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #1a252f, #0d1419)",
                boxShadow: "0 6px 30px rgba(44, 62, 80, 0.6)",
              },
            }}
            aria-label="play video"
          >
            <span>Play Video</span>
            <PlayArrowIcon sx={{ fontSize: { xs: "20px", md: "24px" } }} />
          </Button>
        </Box>
      </Box>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            maxWidth: { xs: "95vw", sm: "90vw", lg: "1200px" },
          },
        }}
      >
        <DialogTitle sx={{ p: 0, m: 0 }}></DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: "transparent" }}>
          <Box
            sx={{
              position: "relative",
              paddingBottom: "56.25%", // 16:9 aspect ratio
              height: 0,
              overflow: "hidden",
            }}
          >
            <iframe
              src={embedVideoLink}
              title={title || "YouTube video"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
